// BrowserViewer.jsx
//
// Live view/control of a Coworker session's browser
// (docs/BROWSER_COWORKER_PLAN.md). Connects to the backend WS proxy
// (/json/tasks/:id/browser/ws) which pipes the execution host's sidecar
// CDP screencast:
//
//   ← {type:"frame", data:"<base64 jpeg>"}   render (canvas on web, Image on native)
//   ← {type:"state", userControl, url}       control badge
//   → {type:"input", kind:"mouse"|"scroll"|"text"|"key", ...}   user input (web)
//   → {type:"navigate", url}                                    URL bar (web, in control)
//   → {type:"hand_back"} / {type:"take_control"}
//
// Web: fixed overlay window, canvas renderer, full mouse/keyboard control.
// Mac: a fixed local WebView displays pixels and forwards user input.
// iOS/Android: watch-only image stream.
//
// Opening an interactive desktop viewer pauses agent browser input; mobile
// viewers attach watch-only and leave Work running. "Hand back"
// resumes the agent. User keystrokes
// are forwarded unlogged end-to-end (they may contain passwords).
import { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Modal, Platform, Pressable, StyleSheet, TextInput, View } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { coworkerApi } from '@/lib/api';
import MacBrowserCanvas from './MacBrowserCanvas';

const VIEWPORT = { width: 1280, height: 800 };
// Screencast frames arrive at up to ~15fps; throttle redraws so a burst
// can't flood React renders on slower devices.
const MIN_FRAME_INTERVAL_MS = 80;

// Web-only frame layer: renders the JPEG stream with a plain <img> (data
// URI) and captures mouse/keyboard on the wrapper. No <canvas>/drawImage —
// react-native-web's canvas handling left it blank; an img per frame is
// trivially reliable at screencast fps.
function BrowserCanvas({ wsRef, userControl, frame }) {
  const wrapRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    const el = wrapRef.current;
    if (!el) return undefined;

    let mouseDown = false;
    const toPage = (e, clamp = false) => {
      // The image is contained within the stage. Map against its actual
      // bounds so letterboxing after a resize doesn't offset remote clicks.
      const rect = imageRef.current?.getBoundingClientRect();
      if (!rect?.width || !rect.height) return null;
      if (!clamp && (e.clientX < rect.left || e.clientX > rect.right
        || e.clientY < rect.top || e.clientY > rect.bottom)) return null;
      return {
        x: Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) * VIEWPORT.width,
        y: Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height)) * VIEWPORT.height,
      };
    };
    const send = (msg) => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'input', ...msg }));
    };
    const sendMouse = (e, action) => {
      if (!userControl) return;
      // A drag released in the padding must still release the remote button.
      const point = toPage(e, action === 'up' && mouseDown);
      if (action === 'down' && point) mouseDown = true;
      if (action === 'up') mouseDown = false;
      if (point) send({ kind: 'mouse', action, ...(action !== 'move' && { button: 'left' }), ...point });
    };

    const handlers = {
      mousemove: (e) => sendMouse(e, 'move'),
      mousedown: (e) => { if (userControl) { e.preventDefault(); el.focus(); sendMouse(e, 'down'); } },
      mouseup: (e) => sendMouse(e, 'up'),
      dblclick: (e) => sendMouse(e, 'dblclick'),
      wheel: (e) => {
        if (!userControl) return;
        e.preventDefault();
        send({ kind: 'scroll', deltaY: e.deltaY, deltaX: e.deltaX });
      },
      keydown: (e) => {
        if (!userControl) return;
        if (e.key.length === 1 || ['Enter', 'Tab', 'Backspace', 'Delete', 'Escape', 'ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown'].includes(e.key)) {
          // don't intercept browser shortcuts (ctrl/cmd combos)
          if (e.ctrlKey || e.metaKey) return;
          e.preventDefault();
          send({ kind: 'key', key: e.key, action: 'press' });
        }
      },
      // Paste must carry the TEXT, not the keystroke — the remote browser's
      // clipboard is empty, so forwarding Ctrl+V would paste nothing. The
      // orchestrator turns kind:"text" into CDP Input.insertText (unlogged;
      // may contain passwords pasted from a password manager).
      paste: (e) => {
        if (!userControl) return;
        e.preventDefault();
        const text = e.clipboardData?.getData('text/plain');
        if (text) send({ kind: 'text', text });
      },
      // Escape belongs to the remote page while this viewport has focus.
      keyup: (e) => { if (userControl && e.key === 'Escape') e.stopPropagation(); },
    };

    el.addEventListener('mousemove', handlers.mousemove);
    el.addEventListener('mousedown', handlers.mousedown);
    el.addEventListener('mouseup', handlers.mouseup);
    el.addEventListener('dblclick', handlers.dblclick);
    el.addEventListener('wheel', handlers.wheel, { passive: false });
    el.addEventListener('keydown', handlers.keydown);
    el.addEventListener('paste', handlers.paste);
    el.addEventListener('keyup', handlers.keyup);
    return () => {
      el.removeEventListener('mousemove', handlers.mousemove);
      el.removeEventListener('mousedown', handlers.mousedown);
      el.removeEventListener('mouseup', handlers.mouseup);
      el.removeEventListener('dblclick', handlers.dblclick);
      el.removeEventListener('wheel', handlers.wheel);
      el.removeEventListener('keydown', handlers.keydown);
      el.removeEventListener('paste', handlers.paste);
      el.removeEventListener('keyup', handlers.keyup);
    };
  }, [wsRef, userControl]);

  return (
    <div
      ref={wrapRef}
      tabIndex={0}
      aria-label="Remote browser viewport"
      style={styles.frameWrap}
    >
      {frame ? (
        <img
          ref={imageRef}
          src={`data:image/jpeg;base64,${frame}`}
          alt="Coworker browser"
          style={styles.frameImg}
        />
      ) : null}
    </div>
  );
}

export default function BrowserViewer({ threadId, onClose, initialWatch = false, embedded = false }) {
  const { colors } = useTheme();
  const isNative = Platform.OS !== 'web';
  const canControl = Platform.OS === 'web' || Platform.OS === 'macos';
  const wsRef = useRef(null);
  const nativeOverlayRef = useRef(null);
  const lastFrameAtRef = useRef(0);
  const [frame, setFrame] = useState(null);
  const [status, setStatus] = useState('connecting'); // connecting|live|error|closed
  const [watchOnly, setWatchOnly] = useState(!canControl || initialWatch);
  const [userControl, setUserControl] = useState(false);
  const [controlHeld, setControlHeld] = useState(false);
  const [url, setUrl] = useState('');
  const [urlDraft, setUrlDraft] = useState('');
  const [navigationError, setNavigationError] = useState(false);
  const urlEditingRef = useRef(false);
  const [connectionEpoch, setConnectionEpoch] = useState(0);

  useEffect(() => {
    if (Platform.OS !== 'macos') return undefined;
    const timer = setTimeout(() => nativeOverlayRef.current?.focus?.(), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let closed = false;
    let ws = null;
    setUserControl(false);
    setControlHeld(false);
    setFrame(null);
    (async () => {
      let wsUrl;
      try {
        // Only desktop viewers can take control; mobile viewers stay read-only.
        wsUrl = await coworkerApi.getBrowserViewerUrl(threadId, { watch: !canControl || watchOnly });
      } catch (_err) {
        if (!closed) setStatus('error');
        return;
      }
      if (closed) return;
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => !closed && setStatus('live');
      ws.onmessage = (ev) => {
        if (closed) return;
        let msg = null;
        try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg.type === 'frame') {
          // Throttle: drop frames that arrive faster than we can draw.
          const now = Date.now();
          if (now - lastFrameAtRef.current < MIN_FRAME_INTERVAL_MS) return;
          lastFrameAtRef.current = now;
          setFrame(msg.data);
        } else if (msg.type === 'navigation_error') {
          setNavigationError(true);
        } else if (msg.type === 'state') {
          setUserControl(!watchOnly && canControl && !!msg.userControl);
          setControlHeld(!!msg.controlHeld);
          if (msg.url) {
            setUrl(msg.url);
            // Don't clobber the bar while the user is typing in it.
            if (!urlEditingRef.current) setUrlDraft(msg.url);
          }
        }
      };
      ws.onclose = () => {
        if (!closed) setStatus((current) => current === 'live' ? 'closed' : 'error');
      };
      ws.onerror = () => { if (!closed) setStatus('error'); };
    })();
    return () => {
      closed = true;
      try { ws && ws.close(); } catch { /* already closed */ }
      wsRef.current = null;
    };
  }, [threadId, connectionEpoch, canControl, watchOnly]);

  const handBack = useCallback(() => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'hand_back' }));
    // Handing back ends the user's browser session — close the window so
    // the focus returns to the thread (the agent resumes control).
    onClose();
  }, [onClose]);

  const navigate = useCallback(() => {
    const ws = wsRef.current;
    let target = urlDraft.trim();
    if (!target) return;
    if (!/^https?:\/\//i.test(target)) {
      // Bare input: treat as a URL, not a search query (the agent does search).
      target = `https://${target}`;
    }
    try { new URL(target); } catch { return; }
    urlEditingRef.current = false;
    if (ws && ws.readyState === WebSocket.OPEN) {
      setNavigationError(false);
      // URL-bar navigation implies control (the hub only accepts it then).
      if (!userControl) ws.send(JSON.stringify({ type: 'take_control' }));
      ws.send(JSON.stringify({ type: 'navigate', url: target }));
    }
  }, [urlDraft, userControl]);

  const takeControl = useCallback(() => {
    if (watchOnly) {
      setStatus('connecting');
      setWatchOnly(false);
      return;
    }
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'take_control' }));
  }, [watchOnly]);

  const reconnect = useCallback(() => {
    setStatus('connecting');
    setConnectionEpoch((epoch) => epoch + 1);
  }, []);

  const errorText = status === 'error'
    ? 'Browser unavailable — the session may not have started its browser yet.'
    : status === 'closed'
      ? 'Browser stream ended.'
      : null;
  const host = (() => {
    try { return new URL(url).hostname.replace(/^www\./, ''); } catch { return ''; }
  })();

  const stage = (
    <View style={[styles.window, isNative ? styles.windowNative : styles.windowWeb, embedded && styles.windowEmbedded, { backgroundColor: colors.solid_color_primary }]}>
      <View style={[styles.titlebar, { borderBottomColor: colors.border_color_secondary }]}>
        <View style={[styles.browserIcon, { backgroundColor: colors.solid_color_secondary }]}>
          <Icon name="window-maximize" size={12} color={colors.text_color_secondary} />
        </View>
        <View style={styles.titleCopy}>
          <Text size="xs" weight="semibold">Cloud browser</Text>
          <Text size="xxs" color="tertiary" numberOfLines={1} style={styles.url}>
            {host || url || (status === 'connecting' ? 'Connecting to Work’s browser…' : status === 'live' ? 'Connected' : 'Not connected')}
          </Text>
        </View>
        {status === 'live' && (
          <View style={[styles.badge, { borderColor: userControl && canControl ? colors.color_success : colors.border_color_secondary }]}>
            <View style={[styles.badgeDot, { backgroundColor: userControl && canControl ? colors.color_success : colors.text_color_tertiary }]} />
            <Text size="xxs" weight="semibold" color={userControl && canControl ? 'primary' : 'secondary'}>
              {!canControl ? 'Watch-only' : userControl ? 'You are in control' : controlHeld ? 'Another window is in control' : 'Work is browsing'}
            </Text>
          </View>
        )}
        <Pressable
          onPress={onClose}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Close cloud browser"
          style={({ hovered, pressed }) => [styles.closeButton, (hovered || pressed) && { backgroundColor: colors.solid_color_secondary }]}
        >
          <Icon name="xmark" size={14} color={colors.text_color_secondary} />
        </Pressable>
      </View>

      {canControl && !watchOnly && status === 'live' && (
        <View style={[styles.urlbar, { borderBottomColor: colors.border_color_secondary, backgroundColor: colors.solid_color_secondary }]}>
          <Icon name="location-arrow" size={11} color={colors.text_color_tertiary} />
          <TextInput
            value={urlDraft}
            onChangeText={setUrlDraft}
            onFocus={() => { urlEditingRef.current = true; }}
            // Go receives focus before its click. Keep the typed target so
            // blur cannot replace it with the current page before navigate.
            onBlur={() => { urlEditingRef.current = false; }}
            onSubmitEditing={navigate}
            placeholder="Enter a URL — your navigation, not the chat's"
            placeholderTextColor={colors.text_color_tertiary}
            autoCapitalize="none"
            autoCorrect={false}
            keyboardType="url"
            spellCheck={false}
            selectTextOnFocus={Platform.OS === 'web'}
            style={[styles.urlInput, { color: colors.text_color_primary }]}
            accessibilityLabel="Browser address bar"
          />
          {urlDraft !== url && (
            <Pressable onPress={navigate} hitSlop={8} accessibilityRole="button" accessibilityLabel="Go to URL" style={styles.goButton}>
              <Icon name="arrow-right" size={13} color={colors.accent_color_primary} />
            </Pressable>
          )}
        </View>
      )}

      {navigationError && status === 'live' && (
        <View accessibilityRole="alert" style={styles.hintRow}>
          <Text size="s" color="secondary">Could not open that page. Check the address and try again.</Text>
        </View>
      )}
      <View style={styles.stage}>
        {status === 'live' ? (
          Platform.OS === 'macos' ? (
            <MacBrowserCanvas wsRef={wsRef} userControl={userControl} frame={frame} />
          ) : isNative ? (
            frame ? (
              <Image
                source={{ uri: `data:image/jpeg;base64,${frame}` }}
                style={styles.nativeFrame}
                contentFit="contain"
                transition={0}
              />
            ) : (
              <View style={styles.center}><ActivityIndicator color={colors.accent_color_primary} /></View>
            )
          ) : (
            <BrowserCanvas wsRef={wsRef} userControl={userControl} frame={frame} />
          )
        ) : status === 'connecting' ? (
          <View style={styles.center}><ActivityIndicator color={colors.color_success} /></View>
        ) : (
          <View style={styles.center}>
            <Icon name="triangle-exclamation" size={16} color={colors.text_color_tertiary} />
            <Text size="s" color="secondary" style={styles.errText}>{errorText}</Text>
            <Button text="Try again" prependIcon="rotate-right" size="small" variant="secondary" onPress={reconnect} />
          </View>
        )}
      </View>

      {canControl && (
        <View style={[styles.controls, { borderTopColor: colors.border_color_secondary }]}>
          {userControl ? (
            <Button text="Hand back to Work" prependIcon="arrow-right-arrow-left" size="small" onPress={handBack} isDisabled={status !== 'live'} />
          ) : (
            <Button text="Take control" prependIcon="hand" size="small" onPress={takeControl} isDisabled={status !== 'live'} />
          )}
          {userControl && (
            <View style={styles.hintRow}>
              <Icon name="lock" size={10} color={colors.text_color_tertiary} />
              <Text size="xxs" color="tertiary" style={styles.hint}>
                Click the page to interact. Your keystrokes go directly to this browser and are never added to the chat.
              </Text>
            </View>
          )}
        </View>
      )}
    </View>
  );

  if (embedded && (Platform.OS === 'web' || Platform.OS === 'macos')) return stage;

  if (Platform.OS === 'macos') {
    // This macOS target has no RCTModalHostView. Use a native overlay in the
    // chat root, as the lightbox does, instead of mounting an unavailable view.
    return (
      <View
        ref={nativeOverlayRef}
        focusable
        accessibilityViewIsModal
        accessibilityLabel="Cloud browser"
        onKeyDown={(event) => { if (!userControl && event.nativeEvent?.key === 'Escape') onClose(); }}
        style={[styles.macosOverlay, { backgroundColor: colors.solid_color_primary }]}
      >
        {stage}
      </View>
    );
  }

  if (isNative) {
    return (
      <Modal visible animationType="slide" onRequestClose={onClose} statusBarTranslucent>
        <View style={[styles.nativeRoot, { backgroundColor: colors.solid_color_primary }]}>
          {stage}
        </View>
      </Modal>
    );
  }

  return (
    // The web modal portals outside the chat's clipping/stacking ancestors.
    <Modal visible transparent animationType="none" onRequestClose={onClose} accessibilityLabel="Cloud browser">
      <View style={styles.overlay}>
        {stage}
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  macosOverlay: { ...StyleSheet.absoluteFillObject, zIndex: 10000 },
  overlay: {
    ...(Platform.OS === 'web' ? { position: 'fixed', inset: 0, zIndex: 9999 } : {}),
    backgroundColor: 'rgba(0,0,0,0.6)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  nativeRoot: {
    flex: 1,
    paddingTop: 40,
  },
  window: {
    width: '92%',
    maxWidth: 960,
    maxHeight: '90%',
    borderRadius: 16,
    overflow: 'hidden',
    ...Platform.select({ web: { boxShadow: '0 24px 80px rgba(0, 0, 0, 0.35)' } }),
  },
  windowNative: {
    width: '100%',
    maxWidth: undefined,
    maxHeight: undefined,
    flex: 1,
    borderRadius: 0,
  },
  windowEmbedded: { width: '100%', maxWidth: undefined, height: '100%', maxHeight: undefined, borderRadius: 0, ...Platform.select({ web: { boxShadow: 'none' } }) },
  windowWeb: {
    height: '90%',
    maxHeight: 760,
    minHeight: 0,
  },
  titlebar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  browserIcon: { width: 30, height: 30, borderRadius: 9, alignItems: 'center', justifyContent: 'center' },
  titleCopy: { flex: 1, minWidth: 0 },
  url: { flex: 1 },
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    borderRadius: 999,
    borderWidth: 1,
    paddingHorizontal: 9,
    paddingVertical: 4,
  },
  badgeDot: { width: 6, height: 6, borderRadius: 99 },
  closeButton: { width: 30, height: 30, borderRadius: 9, alignItems: 'center', justifyContent: 'center', ...Platform.select({ web: { cursor: 'pointer' } }) },
  urlbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderBottomWidth: 1,
  },
  urlInput: {
    flex: 1,
    minWidth: 0,
    paddingVertical: 0,
    paddingHorizontal: 6,
    borderRadius: 7,
    fontSize: 13,
    ...Platform.select({ web: { outlineWidth: 0 } }),
  },
  goButton: { paddingHorizontal: 6, ...Platform.select({ web: { cursor: 'pointer' } }) },
  stage: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: Platform.OS === 'web' ? 0 : 320,
    overflow: 'hidden',
    backgroundColor: '#111',
  },
  frameWrap: {
    position: 'relative',
    width: '100%',
    height: '100%',
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    outlineStyle: 'none',
    cursor: 'default',
    backgroundColor: '#111',
    overflow: 'hidden',
  },
  frameImg: {
    width: 'auto',
    height: 'auto',
    maxWidth: '100%',
    maxHeight: '100%',
    display: 'block',
    pointerEvents: 'none',
    userSelect: 'none',
  },
  nativeFrame: {
    width: '100%',
    height: '100%',
  },
  center: { padding: 40, alignItems: 'center', gap: 8 },
  errText: { textAlign: 'center' },
  controls: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 12,
    paddingVertical: 11,
    borderTopWidth: 1,
    flexWrap: 'wrap',
  },
  hintRow: { flex: 1, minWidth: 220, flexDirection: 'row', alignItems: 'center', gap: 6 },
  hint: { flex: 1 },
});
